import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import Radio from "antd/es/radio";
import Space from "antd/es/space";
import { useIntl } from "react-intl";

import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";

import MultiSelectActivityTypeField from "@app/component/input/MultiSelectActivityTypeField";
import RadioGroup from "@app/component/input/RadioGroup";
import NylasFormActivitySelection from "@app/scene/settings/enum/NylasFormActivitySelection";
import { activityTypeToNylasActivityItem } from "@app/scene/settings/util/activityType";
import { getActivityTypes } from "@app/store/activity";
import { RootState } from "@app/store/rootReducer";
import NylasActivityItem from "@app/types/nylas/NylasActivityItem";

import styles from "./ActivityTypes.module.scss";

interface Props {
  activityTypes: ActivityType[];
  onChange?: (value: NylasActivityItem[]) => void;
  value?: NylasActivityItem[];
}

const ActivityTypes: React.FC<Props> = ({ activityTypes, onChange, value }) => {
  const intl = useIntl();
  const [fakeActivityTypes, setFakeActivityTypes] = useState<ActivityType[]>(
    (value as ActivityType[]) ?? []
  );
  const [areAllTypesSelected, setAllTypesSelected] = useState<NylasFormActivitySelection>(() => {
    const fakeActivityTypesIds = new Set(fakeActivityTypes.map(({ id }) => id));
    return activityTypes.some(({ id }) => !fakeActivityTypesIds.has(id))
      ? NylasFormActivitySelection.SPECIFIC
      : NylasFormActivitySelection.ALL;
  });

  const handleAllTypesSelectedChange = useCallback(
    (value) => {
      const fakeActivityTypesIds = new Set(fakeActivityTypes.map(({ id }) => id));
      onChange?.(
        (value === NylasFormActivitySelection.ALL
          ? activityTypes
          : activityTypes.filter(({ id }) => fakeActivityTypesIds.has(id))
        ).map(activityTypeToNylasActivityItem)
      );
      setAllTypesSelected(value);
    },
    [activityTypes, onChange, fakeActivityTypes, setAllTypesSelected]
  );

  const handleActivityTypesChange = useCallback(
    (activityTypeIds: ActivityType["id"][]) => {
      const filteredTypes = activityTypes.filter(({ id }) => activityTypeIds.includes(id));
      onChange?.(filteredTypes.map(activityTypeToNylasActivityItem));
      setFakeActivityTypes(filteredTypes);
    },
    [activityTypes, onChange, setFakeActivityTypes]
  );

  const selectedTypeIds = useMemo(
    () => (fakeActivityTypes ?? []).map(({ id }) => id),
    [fakeActivityTypes]
  );

  return (
    <div>
      <p>
        {intl.formatMessage({
          id: "settings.calendar.createEditPane.activityTypes.label",
          defaultMessage: "Select activity types to sync from Map My Customers to your calendar",
          description:
            "Settings calendar create/edit pane - activity types - Select activity types",
        })}
      </p>
      <RadioGroup<NylasFormActivitySelection>
        className={styles.syncSpecific}
        onChange={handleAllTypesSelectedChange}
        value={areAllTypesSelected}
      >
        <Space direction="vertical">
          <Radio value={NylasFormActivitySelection.ALL}>
            {intl.formatMessage({
              id: "settings.calendar.createEditPane.activityTypes.syncAll",
              defaultMessage: "Sync all activity types",
              description:
                "Settings calendar create/edit pane - activity types - Sync all activity types",
            })}
          </Radio>
          <Radio value={NylasFormActivitySelection.SPECIFIC}>
            {intl.formatMessage({
              id: "settings.calendar.createEditPane.activityTypes.syncSpecific",
              defaultMessage: "Sync specific activity types",
              description:
                "Settings calendar create/edit pane - activity types - Sync specific activity types",
            })}
          </Radio>
        </Space>
      </RadioGroup>
      {areAllTypesSelected === NylasFormActivitySelection.SPECIFIC && (
        <MultiSelectActivityTypeField
          className={styles.activityTypeSelect}
          onChange={handleActivityTypesChange}
          value={selectedTypeIds}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActivityTypes(state),
});

export default connect(mapStateToProps, {})(ActivityTypes);
